import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { AppUI } from '@seedlang/state';
import { absoluteCenter, fixedCenter, flexCenterColumn, smallText } from '@seedlang/style_mixins';
import Alert from 'components/alert';
import PaymentButton from 'components/button/payment_button';
import PaymentOption from 'components/checkout/payment_option';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import Spinner from 'components/spinner';
import { noop } from 'lodash';
import { autorun, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import Link from 'components/link';
import CheckoutRegistrationStep from './checkout_registration_step';
import withRouteParams from 'utils/with_route_params';

const Wrapper = styled.div`
  ${flexCenterColumn()}
  ${smallText()}
  margin-bottom: ${props => props.isMobile ? '0' : '20px'};
  ${fixedCenter()}
  background: ${props => props.background};
  z-index: ${Theme.z.foreground};
`;

const Title = styled.div`
  display: block;
  font-size: 20px;
  line-height: 24px;
  font-weight: bold;
  text-align: center;
  color: #000;
  margin: ${props => props.margin};
  word-break: break-word;
`;

const Content = styled.div`
  ${flexCenterColumn()}
  ${absoluteCenter()}
  background: ${props => props.background};
  border-radius: ${props => props.isMobile ? 0 : '20px'};
  max-width: ${props => props.isMobile ? '100%' : '500px'};
  max-height: ${props => props.maxHeight};
  height: ${props => props.isMobile ? '100%' : 'auto'};
  margin: ${props => props.isMobile ? '0' : '60px auto'};
  width: 100%;
  z-index: 4;
  padding: 20px;
  overflow: auto;
`;

const InnerContent = styled.div`
${flexCenterColumn()}
  height: 100%;
  width: 100%;
  background: #FFF;
  border-radius: 10px;
`;

const PaymentWrapper = styled.div`
  ${flexCenterColumn()}
  width: 100%;
  padding: 0 20px;
  background: #FFF;
  border-radius: 5px;
`;

const PaymentOptions = styled.div`
  ${flexCenterColumn()}
  width: 100%;
`;

const Info = styled.div`
  font-size: 14px;
  opacity: 0.8;
  margin: 10px 0;
  text-align: center;
`;

const RedirectMessage = styled.div`
  background: white;
  border-radius: 5px;
  padding: 10px;
  font-size: 14px;
  opacity: 0.8;
  margin: 10px 0;
  text-align: center;
  font-weight: bold;
`;

const ErrorMessageWrapper = styled.div`
  margin: 0 0px 10px 0px;
`;

const FailureMessage = styled.span`
  margin-left: 5px;
  background: #ce6322;
`;

@observer
class CheckoutSeedlang extends React.Component {
  @observable failedPayment = false;
  @observable failureMessage;
  @observable paymentType = 'stripe';
  @observable startedCheckoutAsUnregistered = false;
  @observable show = 'checkout';
  @observable registrationComplete = false;
  @observable setViewedCheckoutCount = false;
  @observable authType = null;
  @observable isDataLoaded = false;
  @observable submittingPayment = false;

  constructor(props) {
    super(props);
    if (document.referrer.match('checkout.stripe.com') && AppUI.getCookieValue('redirected-to-stripe')) {
      // checkout.stripe.com stays as the document.referrer even when it's not the last click
      // need to protect against having a loop between memberships.index and this page
      // which would make it impossible to get to the checkout page
      AppUI.setCookieValue('redirected-to-stripe', false);
      AppUI.routeStore.routeToNamed('memberships.index');
    } else {
      if (!AppUI.currentlyViewedMembership || AppUI.currentlyViewedMembership.id !== this.props.params.membershipId) {
        AppUI.membershipStore.getShow({ids: {membershipId: this.props.params.membershipId}});
      }
      AppUI.createEvent('checkout - payment form', {membership_id: this.props.params.membershipId});

      this.disposer = autorun(() => {
        if (AppUI.user && AppUI.user.signedOut) {
          this.startedCheckoutAsUnregistered = true;
          this.show = 'registration';
        } else if (AppUI.userIsLoaded && !this.setViewedCheckoutCount) {
          this.setViewedCheckoutCount = true;
          AppUI.user.set('viewedCheckoutCount', AppUI.user.viewedCheckoutCount + 1);
          AppUI.authUserStore.update({data: {viewedCheckoutCount: AppUI.user.viewedCheckoutCount}}, noop);
        }
      });
    }
  }

  componentWillUnmount() {
    this.disposer && this.disposer();
    AppUI.set('currentlyViewedMembership', null);
  }

  @computed get contentMaxHeight() {
    return AppUI.layout.isMobile ? '100%' : '500px';
  }

  @computed get buttonText() {
    const costFormatted = this.membership.costFormatted && this.membership.costFormatted(AppUI.user.currency, this.quantity);
    if (costFormatted && this.membership.recurring) {
      let str = `Pay ${costFormatted}`;
      str = `${str} per ${this.membership.numberMonths > 1 ? this.membership.numberMonths : 1} Month${this.membership.numberMonths > 1 ? 's' : ''}`;
      return str;
    } else if (costFormatted) {
      return `Pay ${costFormatted} One Time`;
    }
    return '';
  }

  @computed get disablePaymentButton() {
    return (this.startedCheckoutAsUnregistered && !this.registrationComplete);
  }

  @computed get membership() {
    return AppUI.currentlyViewedMembership || AppUI.membershipStore.showData;
  }
  createPayment = () => {
    this.reset();
    this.setSubmittingPayment();
    if (this.paymentType === 'stripe') {
      AppUI.createEvent('checkout - click stripe button');
      this.forwardToStripe();
    } else if (this.paymentType === 'paypal') {
      AppUI.createEvent('checkout - click paypal button');
      AppUI.paypalStore.create({data: {
        membership_id: this.props.params.membershipId,
      }}, this.afterCreatePaypalLink);
    }
  }
  setSubmittingPayment = () => {
    this.submittingPayment = true;
  }
  afterCreatePaypalLink = (resp) => {
    if (resp.success) {
      if (resp.url) {
        window.location = resp.url;
      }
      else {
        console.warn('No url returned from the API while creating a PayPal link.');
        this.afterCreatePaymentFailure(resp);
      }
    }
    else {
      this.afterCreatePaymentFailure(resp);
    }
  }
  afterCreatePaymentFailure = (resp) => {
    AppUI.createEvent('checkout - failed payment', {
      membership_id: this.props.params.membershipId,
      payment_type: this.paymentType,
      message: resp['message'],
    });
    this.submittingPayment = false;
    this.failureMessage = resp['message'];
    this.failedPayment = true;
  }
  reset = () => {
    this.failedPayment = false;
  }
  onSelectPaymentType = (paymentType) => {
    this.paymentType = paymentType;
  }
  afterRegistration = () => {
    AppUI.loadUser();
    this.show = 'checkout';
    this.registrationComplete = true;
  }
  forwardToStripe = async () => {
    AppUI.setCookieValue('redirected-to-stripe', true);
    try {
      const resp = await AppUI.stripeStore.createSessionPromise({data: {
        quantity: 1,
        membership_id: this.props.params.membershipId,
        currency: AppUI.user?.currency,
      }});
      window.location = resp.url;
    } catch (error) {
      if (error.status === 401) {
        this.startedCheckoutAsUnregistered = true;
        this.show = 'registration';
        this.submittingPayment = false;
        return;
      }
      this.afterCreatePaymentFailure({ message: error.message });
    }
  }

  render() {
    return (
      <Wrapper
        className='Checkout__Wrapper'
        background='transparent'
        isMobile={AppUI.layout.isMobile}
      >
        {
          this.membership &&
            <Content
              className='Checkout__Content'
              background='linear-gradient(-134deg, rgb(31, 204, 100) 0%, rgb(69, 119, 94) 100%)'
              maxHeight={this.contentMaxHeight}
              isMobile={AppUI.layout.isMobile}
            >
              <InnerContent className='Checkount__InnerContent'>
                <Title
                  margin='0 0 20px 0'
                >
                  Complete Your Purchase
                </Title>
                {
                  this.show === 'loading' &&
                    <Spinner />
                }

                {
                  this.show === 'registration' &&
                    <CheckoutRegistrationStep afterRegistration={this.afterRegistration} />
                }
                {
                  this.show === 'checkout' &&
                    <PaymentWrapper>
                      {this.renderErrorMessages()}

                      {
                        this.startedCheckoutAsUnregistered && !this.registrationComplete &&
                          <Alert
                            width='370px'
                            borderRadius='5px'
                          >
                            Registration must be completed before you can checkout.
                          </Alert>
                      }
                      <PaymentOptions>
                        <PaymentOption
                          text='Credit Card'
                          selected={this.paymentType === 'stripe'}
                          onClick={() => this.onSelectPaymentType('stripe')}
                        />
                        <PaymentOption
                          text='Paypal'
                          image='/images/paypal.png'
                          selected={this.paymentType === 'paypal'}
                          onClick={() => this.onSelectPaymentType('paypal')}
                        />
                      </PaymentOptions>
                      {
                        this.submittingPayment &&
                          <Spinner className='blue' />
                      }
                      {
                        this.paymentType === 'paypal' && !this.submittingPayment &&
                          <RedirectMessage>
                            You will be redirected to Paypal to complete your payment.
                          </RedirectMessage>
                      }
                      {
                        this.paymentType === 'stripe' && !this.submittingPayment &&
                          <RedirectMessage>
                            You will be redirected to Stripe to complete your payment.
                          </RedirectMessage>
                      }
                      {
                        this.membership &&
                          <PaymentButton
                            onClick={this.createPayment}
                            disabled={this.disablePaymentButton}
                          >
                            {
                              this.paymentType === 'paypal' &&
                                <img
                                  src='/images/paypal.png'
                                  alt='Paypal logo'
                                  style={{
                                    width: 20,
                                    margin: '10px 5px 0 0',
                                  }}
                                />
                            }
                            <span>
                              {this.buttonText}
                            </span>
                          </PaymentButton>
                      }
                      <Info>
                        15 day money-back guarantee.  Cancel anytime.
                      </Info>
                    </PaymentWrapper>
                }
              </InnerContent>
            </Content>
          }

        </Wrapper>
    );
  }

  renderErrorMessages() {
    return (
      <ErrorMessageWrapper className='Checkout__ErrorMessageWrapper'>
        {
          AppUI.user.isMember && !AppUI.isDevelopment &&
            <Alert
              width='100%'
              borderRadius='5px'
            >
              <b>You already have an active membership.</b> If you would like to change your subscription, you can continue and we will cancel your previous subscription for you if it was previously made on the website. <a href='/settings/billing' target='_blank' style={{ textDecoration: 'underline', cursor: 'pointer' }}>View your billing page</a>.
            </Alert>
        }
        {
          this.failedPayment &&
            <Alert
              width='100%'
              borderRadius='5px'
              failure
            >
              <div>
                <span>We are sorry, our payment provider returned this message:</span>
                <FailureMessage>
                  {this.failureMessage}.
                </FailureMessage>
                <br />
                <br />
                <div>
                  <span> Please </span>
                  <Link to='contact'>
                    contact us
                  </Link>
                  <span> if you need assistance.</span>
                </div>
              </div>
            </Alert>
        }
      </ErrorMessageWrapper>
    );
  }
}

export default withRouteParams(ErrorBoundary(CheckoutSeedlang));
